interface MilestoneInfo {
  id: string;
  title: string;
  description: string;
  emoji: string;
  creditsAwarded: number;
  shareText: string;
}

interface Props {
  milestone: MilestoneInfo | null;
  onClose: () => void;
}

const C = {
  surface: '#161627',
  border: '#2a2a3e',
  primary: '#7c6ff7',
  accent: '#00d4ff',
  green: '#22c55e',
  text: '#f0f0ff',
  text2: '#8888aa',
};

export const MILESTONES: Record<string, MilestoneInfo> = {
  first_signal: {
    id: 'first_signal',
    title: 'First Market Signal!',
    description: 'You just did what 80% of founders never do — talked to a real user and logged it.',
    emoji: '',
    creditsAwarded: 1,
    shareText: 'Just logged my first market signal while validating my startup idea.',
  },
  week_one: {
    id: 'week_one',
    title: 'Week 1 Warrior',
    description: '7 days in and still going. Most founders quit before this.',
    emoji: '',
    creditsAwarded: 0,
    shareText: 'Day 7 of validating my startup — still going strong.',
  },
  mvp_unlocked: {
    id: 'mvp_unlocked',
    title: 'Blueprint Unlocked',
    description: 'Your personalized MVP Blueprint is ready — built on your actual results.',
    emoji: '',
    creditsAwarded: 2,
    shareText: 'Just unlocked my personalized MVP Blueprint after 14 days of real validation.',
  },
  first_intelligence: {
    id: 'first_intelligence',
    title: 'Market Intel Activated',
    description: "You're now building with data, not assumptions.",
    emoji: '',
    creditsAwarded: 0,
    shareText: 'Just ran my first market intelligence scan on my startup idea.',
  },
};

export default function MilestoneCelebration({ milestone, onClose }: Props) {
  if (!milestone) return null;

  const shareBase = `${milestone.shareText} — building with @StartupScoreLive #buildinpublic`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareBase)}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://startupscore.live')}`;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1100,
      background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'fadeIn 0.3s ease',
    }} onClick={onClose}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 20, padding: 40, maxWidth: 400, width: '90%', textAlign: 'center',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 64, marginBottom: 16, animation: 'float 3s ease-in-out infinite' }}>
          {milestone.emoji}
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 8 }}>
          {milestone.title}
        </h2>
        <p style={{ fontSize: 14, color: C.text2, lineHeight: 1.6, marginBottom: 16 }}>
          {milestone.description}
        </p>

        {milestone.creditsAwarded > 0 && (
          <div style={{
            display: 'inline-block', background: `${C.green}15`,
            border: `1px solid ${C.green}40`, borderRadius: 20,
            padding: '4px 14px', marginBottom: 20,
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.green }}>
              +{milestone.creditsAwarded} credit{milestone.creditsAwarded > 1 ? 's' : ''}
            </span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
          <a href={twitterUrl} target="_blank" rel="noopener noreferrer" style={{
            background: '#1DA1F220', border: '1px solid #1DA1F240',
            borderRadius: 8, color: '#1DA1F2', fontSize: 12, fontWeight: 600,
            padding: '8px 14px', textDecoration: 'none',
          }}>Share on X</a>
          <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" style={{
            background: '#0A66C220', border: '1px solid #0A66C240',
            borderRadius: 8, color: '#0A66C2', fontSize: 12, fontWeight: 600,
            padding: '8px 14px', textDecoration: 'none',
          }}>Share on LinkedIn</a>
        </div>

        <button onClick={onClose} style={{
          background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
          border: 'none', borderRadius: 10, color: '#fff',
          fontSize: 14, fontWeight: 700, padding: '10px 24px', cursor: 'pointer',
        }}>
          Continue →
        </button>
      </div>
    </div>
  );
}
